import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SecureStorage } from '../../utils/storage';
import { api } from '../../api/remembrApi';
import { perf } from '../../utils/logger';
import type { RootState } from '../../store/store';
import type {
  GroupListItem,
  GroupDetail,
  GroupMember,
  GroupKind,
  GroupMemberRole,
  CreateGroupRequest,
  PatchGroupRequest,
  InviteMemberRequest,
  InviteMemberResponse,
  AcceptInviteResponse,
  // Sprint 2 (Chunk 9) — group inventory + binding management
  GroupItemSummary,
  GroupInventoryResponse,
  // Sprint 4 / Chunk 10B — activity feed
  GroupActivityEvent,
  // backend#114 — pending received invites
  ReceivedInviteOut,
} from '../../api/types';

/**
 * Workspaces / Groups slice — Sprint 1 (Chunk 8).
 *
 * Replaces the AsyncStorage-only mock that shipped pre-Chunk-8. All
 * thunks now hit the real backend (`/groups/*` endpoints) via `api`.
 * Legacy `@founsi/groupsSlice.v1` AsyncStorage key is dropped on first
 * boot of this slice so stale mock data doesn't leak.
 *
 * Two collections in state:
 *   - `groups` — list-tile shape (`GroupListItem[]`) for CrewHubScreen
 *   - `detail` — keyed by group_id; full `GroupDetail` for GroupDetailScreen
 *
 * Selector signatures kept stable for back-compat:
 *   - selectGroups(state) → GroupListItem[]
 *   - selectGroupById(id)(state) → GroupListItem | undefined
 *   - selectGroupDetail(id)(state) → GroupDetail | undefined  (new)
 *   - selectGroupsLoading / selectGroupsError / clearGroupsError
 *
 * Re-exports kind/role/status enums via the api/types path; consumers
 * should import them from `../../api/types` going forward.
 */

// Re-export the type names existing screens already consume so we don't
// have to touch every file.
export type {
  GroupListItem,
  GroupDetail,
  GroupMember,
  GroupKind,
  GroupMemberRole,
  GroupMemberStatus,
} from '../../api/types';

/** Sprint 2 — per-group inventory cache. Keyed by group_id. Each entry
 *  holds the items the user has loaded so far for that group, plus
 *  pagination cursors. The list isn't trimmed when the user navigates
 *  away — they'll likely come back and we'd rather render stale than
 *  flash an empty state. */
export interface GroupInventoryCacheEntry {
  items: GroupItemSummary[];
  total: number;
  /** Highest offset+limit seen so far. Used to compute the next page's
   *  offset for infinite-scroll pagination. */
  nextOffset: number;
  loading: boolean;
  error: string | null;
  /** True once an initial load has resolved (success or fail) — drives
   *  empty-state rendering vs initial spinner. */
  hasLoaded: boolean;
}

/** Sprint 4 / Chunk 10B — per-group activity-feed cache. Keyed by
 *  group_id. Mirrors the inventory cache shape so the FlatList can
 *  paginate via offset cursors. */
export interface GroupActivityCacheEntry {
  events: GroupActivityEvent[];
  total: number;
  nextOffset: number;
  loading: boolean;
  error: string | null;
  hasLoaded: boolean;
}

/**
 * FE-255 PR3 — optimistic-mutation bookkeeping.
 *
 * Snapshot of state taken at the moment an optimistic mutation
 * fires, used to restore on .rejected. Keyed by the thunk's
 * auto-generated `action.meta.requestId`, so two mutations on
 * different rows in flight at the same time roll back independently.
 *
 * Each variant captures only the fields its mutation touched so
 * rollback is byte-for-byte. The `type` discriminator makes the
 * .rejected reducer easy to read — restore-by-type, no big branchy
 * function.
 *
 * Note on `accept-invite`: rollback is gated — when the BE returns
 * the soft-success `ALREADY_REDEEMED` code, we KEEP the optimistic
 * removal (the user is already in the group; restoring would be
 * confusing). For any other error we restore the invite row so the
 * banner shows it again.
 */
type GroupsInflightOperation =
  | {
      type: 'remove-item';
      groupId: number;
      prevItem: any;
      prevIndex: number;
      prevTotal: number;
    }
  | {
      type: 'remove-member' | 'cancel-invite';
      groupId: number;
      prevMember: any;
      prevIndex: number;
    }
  | {
      type: 'accept-invite';
      token: string;
      prevInvite: any;
      prevIndex: number;
    };

interface GroupsState {
  groups: GroupListItem[];
  /** Keyed by group_id (numeric, but Record requires string keys). */
  detail: Record<number, GroupDetail>;
  /** Sprint 2 — per-group inventory cache. */
  inventory: Record<number, GroupInventoryCacheEntry>;
  /** Sprint 4 / Chunk 10B — per-group activity-feed cache. */
  activity: Record<number, GroupActivityCacheEntry>;
  /** backend#114 — pending invites received by the signed-in user. */
  receivedInvites: ReceivedInviteOut[];
  receivedInvitesLoaded: boolean;
  /** Tokens currently being accepted (so the banner row can show a
   *  per-row spinner without blocking the rest of the list). */
  acceptingTokens: string[];
  loading: {
    list: boolean;
    creating: boolean;
    updating: boolean;
    inviting: boolean;
    removing: boolean;
    accepting: boolean;
    receivedInvites: boolean;
  };
  error: string | null;
  /** Flips true once initial loadGroups completes (or fails). Useful for
   *  rendering empty-state vs spinner. */
  hasLoaded: boolean;
  /** FE-255 PR3 — see GroupsInflightOperation comment at top of file. */
  inflight: Record<string, GroupsInflightOperation>;
}

const LEGACY_STORAGE_KEY = '@founsi/groupsSlice.v1';

const initialState: GroupsState = {
  groups: [],
  detail: {},
  inventory: {},
  activity: {},
  receivedInvites: [],
  receivedInvitesLoaded: false,
  acceptingTokens: [],
  loading: {
    list: false,
    creating: false,
    updating: false,
    inviting: false,
    removing: false,
    accepting: false,
    receivedInvites: false,
  },
  error: null,
  hasLoaded: false,
  // FE-255 PR3 — empty until a remove-item / remove-member /
  // cancel-invite / accept-invite fires.
  inflight: {},
};

/* ──────────────────────────────────────────────────────────────────────
 * Thunks
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Drop the legacy AsyncStorage mock-data key that the old groupsSlice
 * persisted. Idempotent — safe to call on every app boot. The new slice
 * is server-backed; we never touch AsyncStorage from here.
 */
export const cleanupLegacyMockStorage = createAsyncThunk(
  'groups/cleanupLegacyMockStorage',
  async () => {
    try {
      const existing = await SecureStorage.getItem(LEGACY_STORAGE_KEY);
      if (existing) {
        await SecureStorage.removeItem(LEGACY_STORAGE_KEY);
        // eslint-disable-next-line no-console
        console.log('[groupsSlice] removed legacy AsyncStorage mock data');
      }
    } catch {
      /* private-mode / storage-unavailable — fine, nothing to do */
    }
  },
);

export const loadGroups = createAsyncThunk<
  GroupListItem[],
  { refresh?: boolean; includeArchived?: boolean } | undefined,
  { state: RootState }
>(
  'groups/loadGroups',
  async (args, { rejectWithValue }) => {
    return perf.span(
      'thunk:loadGroups',
      async () => {
        try {
          return await api.listGroups({
            include_archived: args?.includeArchived,
          });
        } catch (err: any) {
          return rejectWithValue(
            err?.response?.data?.detail || err?.message || 'Failed to load circles',
          );
        }
      },
      { refresh: args?.refresh ?? false, includeArchived: args?.includeArchived ?? false },
    ) as any;
  },
  {
    /**
     * Phase A bug fix — kill the `/groups` console spam on ItemDetail.
     *
     * Several separate call sites dispatch `loadGroups()` (AppNavigator
     * on boot, CrewHubScreen mount, ItemDetailScreen's CustodySectionMount,
     * QuickAddSheet open, SegmentScreen scan, CrewHubScreen
     * refresh/retry). Each callsite has its own
     * `if groups.length === 0` gate, but those gates don't dedupe
     * concurrent dispatches — if two effects fire before the first
     * fetch resolves, both hit the network.
     *
     * This `condition` collapses concurrent + redundant dispatches
     * to a single in-flight request. `refresh: true` always bypasses
     * (used by pull-to-refresh + retry).
     *
     * Net effect: ItemDetail mount that previously fired ~10
     * `/groups` requests now fires 0–1 (0 if app boot already
     * loaded; 1 if we navigated before the first fetch completed).
     */
    condition: (args, { getState }) => {
      if (args?.refresh) return true;
      const slice = getState().groups;
      // In-flight — let the first dispatch win, skip the rest.
      if (slice.loading.list) return false;
      // Already loaded — skip even when `groups.length === 0`.
      // The earlier version checked `hasLoaded && groups.length > 0`,
      // which fell through every time for users who legitimately
      // have zero crews (empty array is a valid loaded state).
      // That bug let the /groups call repeat ~10x per ItemDetail
      // mount for borrower accounts. `hasLoaded` alone is the
      // correct signal — a user with no crews shouldn't keep
      // retrying.
      if (slice.hasLoaded) return false;
      return true;
    },
  },
);

export const loadGroupDetail = createAsyncThunk<GroupDetail, number>(
  'groups/loadGroupDetail',
  async (groupId, { rejectWithValue }) => {
    return perf.span(
      `thunk:loadGroupDetail(${groupId})`,
      async () => {
        try {
          return await api.getGroup(groupId);
        } catch (err: any) {
          return rejectWithValue(
            err?.response?.data?.detail || err?.message || 'Failed to load circle',
          );
        }
      },
    ) as any;
  },
);

export const createGroup = createAsyncThunk<GroupDetail, CreateGroupRequest>(
  'groups/createGroup',
  async (request, { rejectWithValue }) => {
    try {
      return await api.createGroup(request);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Failed to create circle',
      );
    }
  },
);

export const patchGroup = createAsyncThunk<
  GroupDetail,
  { groupId: number; patch: PatchGroupRequest }
>('groups/patchGroup', async ({ groupId, patch }, { rejectWithValue }) => {
  try {
    return await api.patchGroup(groupId, patch);
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.detail || err?.message || 'Failed to update circle',
    );
  }
});

/** Soft-archive a group (server marks `archived_at`; members keep
 *  history but lose access to subsequent endpoints). v1 has no hard
 *  delete on the wire — `deleteGroup` was the legacy mock name. */
/** FE-219 — archive AND un-archive in one thunk. Server enforces:
 *  archived=true blocks new invites / handoffs / item bindings;
 *  archived=false re-opens normal operation. Existing data is
 *  preserved either way (per I-6 invariant). */
export const archiveGroup = createAsyncThunk<
  GroupDetail,
  { groupId: number; archived: boolean } | number
>(
  'groups/archiveGroup',
  async (arg, { rejectWithValue }) => {
    // Back-compat: legacy callers pass `groupId` directly meaning archive=true.
    const { groupId, archived } =
      typeof arg === 'number' ? { groupId: arg, archived: true } : arg;
    try {
      return await api.patchGroup(groupId, { archived });
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail ||
          err?.message ||
          (archived ? 'Failed to archive circle' : 'Failed to un-archive circle'),
      );
    }
  },
);

export const inviteGroupMember = createAsyncThunk<
  InviteMemberResponse & { group_id: number },
  { groupId: number; request: InviteMemberRequest }
>('groups/inviteGroupMember', async ({ groupId, request }, { rejectWithValue }) => {
  try {
    const res = await api.inviteGroupMember(groupId, request);
    return { ...res, group_id: groupId };
  } catch (err: any) {
    // FE-232 — preserve the structured payload so the caller can
    // surface a code-specific UX (e.g. the 429 invite_cooldown modal
    // with countdown). Falls back to the legacy string when the
    // server didn't return a structured body.
    const data = err?.response?.data;
    if (data?.code) {
      return rejectWithValue(
        // We pass the structured envelope; callers that just want a
        // string can read `.detail` off it. parseInviteCooldown() etc.
        // read `.code` + `.context`.
        {
          detail: data.detail || err?.message || 'Failed to send invite',
          code: data.code,
          context: data.context,
          status: err?.response?.status,
        } as any,
      );
    }
    return rejectWithValue(
      err?.response?.data?.detail || err?.message || 'Failed to send invite',
    );
  }
});

export const removeGroupMember = createAsyncThunk<
  { group_id: number; user_id: number },
  { groupId: number; userId: number }
>('groups/removeGroupMember', async ({ groupId, userId }, { rejectWithValue }) => {
  try {
    await api.removeGroupMember(groupId, userId);
    return { group_id: groupId, user_id: userId };
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.detail || err?.message || 'Failed to remove member',
    );
  }
});

/** FE-216 / BE-212 — cancel a pending invite by email. Pre-INVITED rows
 *  have no user_id yet, so the BE keyed the deletion path on the email
 *  instead. On success, the slice drops the matching member row from
 *  detail.members so the UI updates without a refetch. */
export const cancelGroupInviteByEmail = createAsyncThunk<
  { group_id: number; email: string },
  { groupId: number; email: string }
>(
  'groups/cancelGroupInviteByEmail',
  async ({ groupId, email }, { rejectWithValue }) => {
    try {
      await api.cancelGroupInviteByEmail(groupId, email);
      return { group_id: groupId, email: email.toLowerCase() };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail ||
          err?.message ||
          'Could not cancel the invite',
      );
    }
  },
);

export const patchGroupMemberRole = createAsyncThunk<
  { group_id: number; member: GroupMember },
  { groupId: number; userId: number; role: GroupMemberRole }
>(
  'groups/patchGroupMemberRole',
  async ({ groupId, userId, role }, { rejectWithValue }) => {
    try {
      const member = await api.patchGroupMemberRole(groupId, userId, { role });
      return { group_id: groupId, member };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Failed to change role',
      );
    }
  },
);

export const acceptGroupInvite = createAsyncThunk<AcceptInviteResponse, string>(
  'groups/acceptGroupInvite',
  async (token, { rejectWithValue }) => {
    try {
      return await api.acceptGroupInvite({ invite_token: token });
    } catch (err: any) {
      // Surface a structured payload so the rejected reducer + the
      // calling screen can distinguish "already-redeemed" / "not-found"
      // from a real error. The shape `{ code, detail, status, context }`
      // is serializable for redux (no Error instance leaks).
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to accept invite';
      const beCode: string | undefined = err?.response?.data?.code;
      const beContext = err?.response?.data?.context;
      const lowerDetail = String(detail).toLowerCase();

      // SOFT SUCCESS — the user is already a member:
      //
      // 1) Pre-BE-203 path: 404 with "already redeemed" / "not found"
      //    in the detail string. Heuristic match for back-compat.
      //
      // 2) Post-BE-203 / SG-17 path: structured 410 + code values:
      //      - `invite_consumed` — token was already accepted (the
      //        common race when the user double-taps Accept or the
      //        Accept-button is re-fired by a React re-render).
      //      - `invite_expired` — token timed out, but if the user
      //        is ALREADY in the group (per BE-209 re-invite-after-
      //        remove flow) this still means "soft success" from
      //        the UX perspective. The reducer downstream removes
      //        the token from receivedInvites either way.
      //
      // The caller treats ALREADY_REDEEMED as "fine, you're in" and
      // surfaces a friendly toast instead of a scary error.
      const alreadyRedeemed =
        (status === 404 &&
          (lowerDetail.includes('already redeemed') ||
            lowerDetail.includes('not found'))) ||
        (status === 410 && beCode === 'invite_consumed') ||
        (status === 410 && beCode === 'invite_expired');

      return rejectWithValue({
        code: alreadyRedeemed ? 'ALREADY_REDEEMED' : beCode || 'ERROR',
        detail,
        status,
        context: beContext,
      } as any);
    }
  },
);

/** backend#114 — fetch the signed-in user's pending received invites
 *  for the CrewHub banner. Idempotent; the screen typically calls it
 *  alongside loadGroups on mount. */
export const loadReceivedInvites = createAsyncThunk<
  ReceivedInviteOut[],
  void,
  { rejectValue: string }
>(
  'groups/loadReceivedInvites',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.getReceivedInvites();
      return res.invites ?? [];
    } catch (err: any) {
      // Quietly degrade — invites are a nice-to-have surface and we
      // don't want a hot endpoint failure to crash the CrewHub render.
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Failed to load invites',
      );
    }
  },
);

/* ──────────────────────────────────────────────────────────────────────
 * Sprint 2 (Chunk 9) — group inventory + binding management
 * ────────────────────────────────────────────────────────────────────── */

/** Load (or refresh / paginate) a group's inventory. The reducer
 *  appends or replaces based on the `mode` argument:
 *
 *   - 'replace' (default for a fresh load) — wipes the cached items
 *     and inserts the new page. Use when the user pulls-to-refresh
 *     or when the tab is opened cold.
 *   - 'append' — adds the new page after the existing items.
 *     Use for infinite-scroll pagination.
 *
 *  Idempotent at the offset/limit level: calling with the same
 *  cursors twice doesn't double-render because the reducer dedupes
 *  by item_id when in 'append' mode.
 */
export const loadGroupInventory = createAsyncThunk<
  { groupId: number; data: GroupInventoryResponse; mode: 'replace' | 'append' },
  { groupId: number; limit?: number; offset?: number; mode?: 'replace' | 'append' }
>(
  'groups/loadGroupInventory',
  async (
    { groupId, limit = 50, offset = 0, mode = offset === 0 ? 'replace' : 'append' },
    { rejectWithValue },
  ) => {
    try {
      const data = await api.getGroupInventory(groupId, { limit, offset });
      return { groupId, data, mode };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Failed to load inventory',
      );
    }
  },
);

/** Unbind an item from a group. The item itself stays in the user's
 *  inventory — only the `item_group` row is dropped. Optimistic UI
 *  pattern: the reducer drops the row from the cache on `pending`
 *  and restores it on `rejected`. */
export const removeItemFromGroup = createAsyncThunk<
  { groupId: number; itemId: number },
  { groupId: number; itemId: number }
>(
  'groups/removeItemFromGroup',
  async ({ groupId, itemId }, { rejectWithValue }) => {
    try {
      await api.removeItemFromGroup(groupId, itemId);
      return { groupId, itemId };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Failed to remove item',
      );
    }
  },
);

/** Bulk-add items to a group. Used by Browse multi-select (Sprint 5)
 *  and by FE-9.1's picker confirm path indirectly via /confirm/batch.
 *  Surfaces here so single-screen flows (e.g. "Add to crew" from
 *  ItemDetail) can dispatch without going through confirm/batch. */
export const addItemsToGroup = createAsyncThunk<
  { groupId: number; added: number; skipped: number[] },
  { groupId: number; itemIds: number[] }
>(
  'groups/addItemsToGroup',
  async ({ groupId, itemIds }, { rejectWithValue }) => {
    try {
      const res = await api.addItemsToGroup(groupId, { item_ids: itemIds });
      return { groupId, added: res.added, skipped: res.skipped };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Failed to add items to circle',
      );
    }
  },
);

/** Sprint 4 / Chunk 10B (FE-10.6) — load a page of the group's
 *  activity feed. Two modes:
 *    - 'replace' wipes the cache and inserts the page (pull-to-refresh)
 *    - 'append'  adds the page after existing rows (infinite scroll)
 *
 *  The backend response is denormalized (actor / from / to user names
 *  + item name pre-joined), so the FlatList renderer doesn't need to
 *  cross-reference any other slice. */
const ACTIVITY_PAGE_SIZE = 30;

export const loadGroupActivity = createAsyncThunk<
  {
    groupId: number;
    events: GroupActivityEvent[];
    total: number;
    nextOffset: number;
    mode: 'replace' | 'append';
  },
  {
    groupId: number;
    limit?: number;
    offset?: number;
    mode?: 'replace' | 'append';
  }
>(
  'groups/loadGroupActivity',
  async (
    {
      groupId,
      limit = ACTIVITY_PAGE_SIZE,
      offset = 0,
      mode = offset === 0 ? 'replace' : 'append',
    },
    { rejectWithValue },
  ) => {
    try {
      const data = await api.getGroupActivity(groupId, { limit, offset });
      return {
        groupId,
        events: data.events,
        total: data.total,
        nextOffset: offset + data.events.length,
        mode,
      };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Failed to load activity',
      );
    }
  },
);

/* ──────────────────────────────────────────────────────────────────────
 * Slice
 * ────────────────────────────────────────────────────────────────────── */

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // loadGroups
      .addCase(loadGroups.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(loadGroups.fulfilled, (state, action) => {
        state.loading.list = false;
        state.groups = action.payload;
        state.hasLoaded = true;
      })
      .addCase(loadGroups.rejected, (state, action) => {
        state.loading.list = false;
        state.hasLoaded = true;
        state.error = (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // loadGroupDetail
      .addCase(loadGroupDetail.fulfilled, (state, action) => {
        state.detail[action.payload.id] = action.payload;
      })
      .addCase(loadGroupDetail.rejected, (state, action) => {
        state.error = (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // createGroup
      .addCase(createGroup.pending, (state) => {
        state.loading.creating = true;
        state.error = null;
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.loading.creating = false;
        const detail = action.payload;
        state.detail[detail.id] = detail;
        // Also append to groups list for instant tile rendering.
        state.groups.unshift(toListItem(detail));
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.loading.creating = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // patchGroup
      .addCase(patchGroup.pending, (state) => {
        state.loading.updating = true;
      })
      .addCase(patchGroup.fulfilled, (state, action) => {
        state.loading.updating = false;
        const detail = action.payload as any;
        if (!detail || typeof detail.id !== 'number') return;
        // BE shape regression guard — PATCH /groups/{id} may return only
        // the touched fields (e.g. `{id, name, kind, ...}` without the
        // nested `members` array). Replacing wholesale would wipe the
        // member list and crash the members tab on the next render
        // (`detail.members.map(...)` → undefined). Merge instead so any
        // fields the BE omitted survive from the previous cached copy.
        const previous = state.detail[detail.id];
        state.detail[detail.id] = previous
          ? { ...previous, ...detail, members: detail.members ?? previous.members }
          : detail;
        // Sync the list-tile entry too. Uses the merged detail so the
        // tile name/kind reflect the patch even when the BE returned a
        // partial body.
        const merged = state.detail[detail.id];
        const idx = state.groups.findIndex((g) => g.id === detail.id);
        if (idx !== -1) state.groups[idx] = toListItem(merged);
      })
      .addCase(patchGroup.rejected, (state, action) => {
        state.loading.updating = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // archiveGroup — same shape as patchGroup.fulfilled but also drop from
      // active list. archived_at is now non-null.
      .addCase(archiveGroup.fulfilled, (state, action) => {
        const detail = action.payload;
        state.detail[detail.id] = detail;
        state.groups = state.groups.filter((g) => g.id !== detail.id);
      })

      // inviteGroupMember — re-fetch detail to surface the new INVITED row.
      .addCase(inviteGroupMember.pending, (state) => {
        state.loading.inviting = true;
        state.error = null;
      })
      .addCase(inviteGroupMember.fulfilled, (state) => {
        state.loading.inviting = false;
        // The screen that triggered this should dispatch loadGroupDetail
        // afterward to refresh the members list. We keep the slice
        // mutation minimal here.
      })
      .addCase(inviteGroupMember.rejected, (state, action) => {
        state.loading.inviting = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // removeGroupMember — FE-255 PR3: optimistic + snapshot rollback
      .addCase(removeGroupMember.pending, (state, action) => {
        state.loading.removing = true;
        const { groupId, userId } = action.meta.arg as {
          groupId: number;
          userId: number;
        };
        const detail = state.detail[groupId];
        if (!detail) return;
        const prevIndex = detail.members.findIndex(
          (m) => m.user_id === userId,
        );
        if (prevIndex === -1) return;
        state.inflight[action.meta.requestId] = {
          type: 'remove-member',
          groupId,
          prevMember: detail.members[prevIndex],
          prevIndex,
        };
        detail.members.splice(prevIndex, 1);
      })
      .addCase(removeGroupMember.fulfilled, (state, action) => {
        state.loading.removing = false;
        delete state.inflight[action.meta.requestId];
      })
      .addCase(removeGroupMember.rejected, (state, action) => {
        state.loading.removing = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Error';
        const op = state.inflight[action.meta.requestId];
        if (!op || op.type !== 'remove-member') return;
        const detail = state.detail[op.groupId];
        if (detail) {
          const safeIdx = Math.min(op.prevIndex, detail.members.length);
          detail.members.splice(safeIdx, 0, op.prevMember);
        }
        delete state.inflight[action.meta.requestId];
      })

      // FE-216 / BE-212 — cancel-invite-by-email. FE-255 PR3: now
      // optimistic — the row vanishes on .pending and restores on
      // .rejected. Drops only INVITED-only rows (user_id == null)
      // so an ACTIVE member sharing an email with a pending invite
      // is never accidentally removed.
      .addCase(cancelGroupInviteByEmail.pending, (state, action) => {
        const { groupId, email } = action.meta.arg;
        const detail = state.detail[groupId];
        if (!detail) return;
        const normalized = email.toLowerCase();
        const prevIndex = detail.members.findIndex(
          (m) =>
            (m.invitee_email ?? m.email ?? '').toLowerCase() === normalized &&
            m.user_id == null,
        );
        if (prevIndex === -1) return;
        state.inflight[action.meta.requestId] = {
          type: 'cancel-invite',
          groupId,
          prevMember: detail.members[prevIndex],
          prevIndex,
        };
        detail.members.splice(prevIndex, 1);
      })
      .addCase(cancelGroupInviteByEmail.fulfilled, (state, action) => {
        delete state.inflight[action.meta.requestId];
      })
      .addCase(cancelGroupInviteByEmail.rejected, (state, action) => {
        const op = state.inflight[action.meta.requestId];
        state.error =
          (action.payload as string) ?? action.error.message ?? 'Couldn’t cancel invite';
        if (!op || op.type !== 'cancel-invite') return;
        const detail = state.detail[op.groupId];
        if (detail) {
          const safeIdx = Math.min(op.prevIndex, detail.members.length);
          detail.members.splice(safeIdx, 0, op.prevMember);
        }
        delete state.inflight[action.meta.requestId];
      })

      // patchGroupMemberRole
      .addCase(patchGroupMemberRole.fulfilled, (state, action) => {
        const detail = state.detail[action.payload.group_id];
        if (detail) {
          const idx = detail.members.findIndex(
            (m) => m.user_id === action.payload.member.user_id,
          );
          if (idx !== -1) detail.members[idx] = action.payload.member;
        }
      })

      // acceptGroupInvite — FE-255 PR3: optimistically clear the
      // invite banner row on .pending. The full group detail isn't
      // known until .fulfilled, so we can't synthesize the group
      // row — but the visible win is dropping the banner immediately
      // (users tap Accept then expect the banner to disappear).
      .addCase(acceptGroupInvite.pending, (state, action) => {
        state.loading.accepting = true;
        state.error = null;
        const token = action.meta.arg;
        // Track per-token acceptance so the banner row can spinner.
        // (Kept around in case a render leaks before the optimistic
        // drop takes effect, and as a hook for the rare "BE took the
        // accept but failed to return detail" path.)
        if (token && !state.acceptingTokens.includes(token)) {
          state.acceptingTokens.push(token);
        }
        if (!token) return;
        const prevIndex = state.receivedInvites.findIndex(
          (i) => i.invite_token === token,
        );
        if (prevIndex === -1) return;
        state.inflight[action.meta.requestId] = {
          type: 'accept-invite',
          token,
          prevInvite: state.receivedInvites[prevIndex],
          prevIndex,
        };
        state.receivedInvites.splice(prevIndex, 1);
      })
      .addCase(acceptGroupInvite.fulfilled, (state, action) => {
        state.loading.accepting = false;
        // The BE response shape evolved with the rev-3 batch — depending
        // on the deploy you might receive EITHER:
        //   1. { group: GroupDetail, membership?: ... }   (legacy wrapper)
        //   2. GroupDetail directly                       (current shape)
        // Coalesce to GroupDetail defensively so this reducer doesn't
        // throw "Cannot read properties of undefined (reading 'id')"
        // when the BE flipped to shape #2. (Throwing here used to
        // silently abort the success path — user IS in the group
        // server-side but the FE thinks it failed.)
        const payload = action.payload as any;
        const detail = (payload?.group ?? payload) as
          | { id?: number; [k: string]: any }
          | undefined;
        if (detail && typeof detail.id === 'number') {
          state.detail[detail.id] = detail as any;
          if (!state.groups.find((g) => g.id === detail.id)) {
            state.groups.unshift(toListItem(detail as any));
          }
        }
        // FE-255 PR3 — the invite was already optimistically dropped
        // from receivedInvites in .pending. We keep the .filter() call
        // below as a defensive idempotent no-op in case .pending was
        // skipped (e.g., token missing) so the banner still ends up
        // consistent. Just clear the spinner-tracking token.
        const token = action.meta.arg;
        if (token) {
          state.receivedInvites = state.receivedInvites.filter(
            (i) => i.invite_token !== token,
          );
          state.acceptingTokens = state.acceptingTokens.filter(
            (t) => t !== token,
          );
        }
        delete state.inflight[action.meta.requestId];
      })
      .addCase(acceptGroupInvite.rejected, (state, action) => {
        state.loading.accepting = false;
        // Payload is either the legacy string OR the structured shape
        // we added in PR31 ({ code, detail, status }). Extract the
        // user-facing detail for the error banner.
        const payload = action.payload as unknown;
        const isStructured =
          payload && typeof payload === 'object' && 'code' in (payload as any);
        const detail =
          (isStructured ? (payload as any).detail : (payload as string)) ??
          action.error.message ??
          'Error';
        const isAlreadyRedeemed =
          isStructured && (payload as any).code === 'ALREADY_REDEEMED';

        // PR32 — Critical: only set state.error for GENUINE failures.
        // ALREADY_REDEEMED is a soft success (user is already a member),
        // and CrewHubScreen reads this same `error` field to render the
        // "Couldn't load your groups" banner — so leaking the
        // "Invite not found or already redeemed" string into a sibling
        // screen's error UI is the bug we're fixing here.
        if (!isAlreadyRedeemed) {
          state.error = detail;
        }

        const token = action.meta.arg;
        if (token) {
          state.acceptingTokens = state.acceptingTokens.filter(
            (t) => t !== token,
          );
        }

        // FE-255 PR3 — rollback gate.
        //
        // ALREADY_REDEEMED is a soft success — the user is already a
        // member. The optimistic banner drop from .pending stays
        // (restoring would be confusing UX, and PR31 already explicitly
        // wants the row gone in this case).
        //
        // Every other rejection means the BE truly didn't take the
        // accept, so we restore the banner row from the snapshot.
        const op = state.inflight[action.meta.requestId];
        if (op && op.type === 'accept-invite' && !isAlreadyRedeemed) {
          const safeIdx = Math.min(
            op.prevIndex,
            state.receivedInvites.length,
          );
          state.receivedInvites.splice(safeIdx, 0, op.prevInvite);
        }
        delete state.inflight[action.meta.requestId];
      })
      // backend#114 — pending received invites
      .addCase(loadReceivedInvites.pending, (state) => {
        state.loading.receivedInvites = true;
      })
      .addCase(loadReceivedInvites.fulfilled, (state, action) => {
        state.loading.receivedInvites = false;
        state.receivedInvitesLoaded = true;
        state.receivedInvites = action.payload;
      })
      .addCase(loadReceivedInvites.rejected, (state, _action) => {
        state.loading.receivedInvites = false;
        // Don't surface — the banner just stays empty on failure.
        state.receivedInvitesLoaded = true;
      })
      // ── Sprint 2 — group inventory ─────────────────────────────────
      .addCase(loadGroupInventory.pending, (state, action) => {
        const groupId = action.meta.arg.groupId;
        if (!state.inventory[groupId]) {
          state.inventory[groupId] = emptyInventoryEntry();
        }
        state.inventory[groupId].loading = true;
        state.inventory[groupId].error = null;
      })
      .addCase(loadGroupInventory.fulfilled, (state, action) => {
        const { groupId, data, mode } = action.payload;
        const entry = state.inventory[groupId] ?? emptyInventoryEntry();
        if (mode === 'replace') {
          entry.items = data.items;
        } else {
          // Dedupe by item_id when appending — paginate calls with the
          // same offset can produce overlapping pages on slow nets.
          const seen = new Set(entry.items.map((i) => i.item_id));
          const fresh = data.items.filter((i) => !seen.has(i.item_id));
          entry.items = [...entry.items, ...fresh];
        }
        entry.total = data.total;
        entry.nextOffset = data.offset + data.limit;
        entry.loading = false;
        entry.hasLoaded = true;
        entry.error = null;
        state.inventory[groupId] = entry;
      })
      .addCase(loadGroupInventory.rejected, (state, action) => {
        const groupId = action.meta.arg.groupId;
        const entry = state.inventory[groupId] ?? emptyInventoryEntry();
        entry.loading = false;
        entry.hasLoaded = true;
        entry.error =
          (action.payload as string) ?? action.error.message ?? 'Error';
        state.inventory[groupId] = entry;
      })
      // FE-255 PR3 — proper rollback. Pre-PR3 this thunk had an
      // optimistic .pending drop but the .rejected handler ONLY set
      // an error banner, leaving the row gone in the cache. The
      // user saw their row vanish, then a "couldn't remove" toast,
      // and had to pull-to-refresh to get the row back. Now we
      // snapshot the row + its index + total on .pending and restore
      // them byte-for-byte on .rejected.
      .addCase(removeItemFromGroup.pending, (state, action) => {
        const { groupId, itemId } = action.meta.arg;
        const entry = state.inventory[groupId];
        if (!entry) return;
        const prevIndex = entry.items.findIndex((i) => i.item_id === itemId);
        if (prevIndex === -1) return;
        const prevItem = entry.items[prevIndex];
        state.inflight[action.meta.requestId] = {
          type: 'remove-item',
          groupId,
          prevItem,
          prevIndex,
          prevTotal: entry.total,
        };
        entry.items.splice(prevIndex, 1);
        entry.total = Math.max(0, entry.total - 1);
      })
      .addCase(removeItemFromGroup.fulfilled, (state, action) => {
        // Optimistic drop already happened in .pending — just clear
        // the snapshot so the inflight map doesn't leak.
        delete state.inflight[action.meta.requestId];
      })
      .addCase(removeItemFromGroup.rejected, (state, action) => {
        const op = state.inflight[action.meta.requestId];
        state.error =
          (action.payload as string) ?? action.error.message ?? 'Couldn’t remove item';
        if (!op || op.type !== 'remove-item') return;
        const entry = state.inventory[op.groupId];
        if (entry) {
          // Clamp insert position if the list shrank while in flight
          // (other deletions arrived). Re-insert at the original
          // index when possible, otherwise append.
          const safeIdx = Math.min(op.prevIndex, entry.items.length);
          entry.items.splice(safeIdx, 0, op.prevItem);
          entry.total = op.prevTotal;
        }
        delete state.inflight[action.meta.requestId];
      })
      .addCase(addItemsToGroup.fulfilled, (state, action) => {
        // Mutate the inventory cache for this group: bump total by
        // `added` count. The fresh rows themselves aren't in the
        // payload (server returns just counts) — caller is expected
        // to refetch the page if they care about seeing the newly
        // bound items in the list. Sprint 5 multi-select bulk-add will
        // dispatch loadGroupInventory(replace) right after.
        const { groupId, added } = action.payload;
        const entry = state.inventory[groupId];
        if (entry) entry.total += added;
      })
      // Sprint 4 / Chunk 10B FE-10.6 — activity feed pagination.
      .addCase(loadGroupActivity.pending, (state, action) => {
        const { groupId } = action.meta.arg;
        if (!state.activity[groupId]) state.activity[groupId] = emptyActivityEntry();
        state.activity[groupId].loading = true;
        state.activity[groupId].error = null;
      })
      .addCase(loadGroupActivity.fulfilled, (state, action) => {
        const { groupId, events, total, nextOffset, mode } = action.payload;
        if (!state.activity[groupId]) state.activity[groupId] = emptyActivityEntry();
        const entry = state.activity[groupId];
        entry.loading = false;
        entry.hasLoaded = true;
        entry.error = null;
        entry.total = total;
        entry.nextOffset = nextOffset;
        if (mode === 'replace') {
          entry.events = events;
        } else {
          // append, dedupe by event id
          const seen = new Set(entry.events.map((e) => e.id));
          entry.events = [...entry.events, ...events.filter((e) => !seen.has(e.id))];
        }
      })
      .addCase(loadGroupActivity.rejected, (state, action) => {
        const { groupId } = action.meta.arg;
        if (!state.activity[groupId]) state.activity[groupId] = emptyActivityEntry();
        state.activity[groupId].loading = false;
        state.activity[groupId].hasLoaded = true;
        state.activity[groupId].error =
          (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // ── FE-255 (#49/#59/#69) — optimistic custody flip on approve ────
      //
      // When the owner APPROVES an in-crew borrow request, the item
      // passes into the requester's custody. The owner's GroupDetail
      // inventory row's CTA must flip "Borrow"/"With you" → "Lent out"
      // WITHOUT a manual re-navigation. The approveBorrowRequest thunk
      // (in borrowRequestsSlice) already dispatches a loadGroupInventory
      // refetch for the authoritative reconcile, but the network round-
      // trip is visible — so we ALSO flip the cached row here, the
      // instant the approve resolves, with zero latency.
      //
      // We match on the action *type string* (not the imported thunk's
      // `.fulfilled` action-creator) deliberately: groupsSlice must NOT
      // import borrowRequestsSlice, or we'd create a module cycle
      // (borrowRequestsSlice already imports loadGroupInventory from
      // here). The string is the stable RTK-generated type prefix.
      //
      // The fulfilled payload carries the resolved ids:
      //   { groupId, itemId, borrowerUserId, response }
      // groupId === null ⇒ marketplace request (no group cache to
      // touch) — we no-op, preserving the marketplace path untouched.
      .addMatcher(
        (action: any) =>
          action?.type === 'borrowRequests/approveBorrowRequest/fulfilled',
        (state, action: any) => {
          const payload = action.payload as {
            groupId: number | null;
            itemId: number | null;
            borrowerUserId: number | null;
          } | undefined;
          const groupId = payload?.groupId ?? null;
          const itemId = payload?.itemId ?? null;
          // Marketplace (group_id null) or missing ids — nothing to flip.
          if (groupId == null || itemId == null) return;
          const entry = state.inventory[groupId];
          if (!entry) return; // group inventory never loaded — refetch handles it
          const idx = entry.items.findIndex((i) => i.item_id === itemId);
          if (idx === -1) return;
          const row = entry.items[idx];
          const borrowerUserId = payload?.borrowerUserId ?? null;
          // Whether the borrower is an ACTIVE member of THIS group drives
          // the precise availability_state: 'in_use_by_member' exposes
          // the custodian id (SG-2 allows it for in-group custodians);
          // 'lent_out' hides it. Both make `itemIsOutWithSomeoneElse`
          // true in GroupInventorySection → CTA renders "Lent out".
          const detail = state.detail[groupId];
          const borrowerInGroup =
            borrowerUserId != null &&
            (detail?.members ?? []).some(
              (m) => m.user_id === borrowerUserId && m.status === 'ACTIVE',
            );
          row.availability_state = borrowerInGroup
            ? 'in_use_by_member'
            : 'lent_out';
          row.is_currently_out = true;
          // Surface the custodian id only when SG-2 permits (in-group).
          row.current_custodian_user_id = borrowerInGroup
            ? borrowerUserId
            : null;
          // Optimistic custody-since — the refetch overwrites with the
          // server's exact timestamp on the next tick.
          row.custody_since = new Date().toISOString();
        },
      );
  },
});

/** Initial state for an inventory cache entry — used by reducers
 *  before the first load lands. */
function emptyInventoryEntry(): GroupInventoryCacheEntry {
  return {
    items: [],
    total: 0,
    nextOffset: 0,
    loading: false,
    error: null,
    hasLoaded: false,
  };
}

/** Initial state for an activity cache entry — same shape as inventory. */
function emptyActivityEntry(): GroupActivityCacheEntry {
  return {
    events: [],
    total: 0,
    nextOffset: 0,
    loading: false,
    error: null,
    hasLoaded: false,
  };
}

/** Project a `GroupDetail` (or bare `GroupBase` from POST /groups) down
 *  to a list-tile shape for the groups[] array. Sprint 1 reality:
 *  `members` may be absent on the create response (backend#103) — guard
 *  with `?? []`. `my_role` derives from owner_user_id when not provided
 *  by the server (Sprint 1 backend doesn't yet enrich list rows). */
function toListItem(
  detail: GroupDetail | (Omit<GroupDetail, 'members' | 'my_role'> & {
    members?: GroupMember[];
    my_role?: GroupListItem['my_role'];
  }),
): GroupListItem {
  const members = ('members' in detail && detail.members) || [];
  return {
    id: detail.id,
    name: detail.name,
    kind: detail.kind,
    owner_user_id: detail.owner_user_id,
    description: detail.description ?? null,
    auto_approve_handoff: detail.auto_approve_handoff,
    archived_at: detail.archived_at,
    created_at: detail.created_at,
    updated_at: detail.updated_at,
    my_role: detail.my_role,
    member_count: members.filter((m) => m.status === 'ACTIVE').length || undefined,
    item_count: 0, // populated by GET /groups list response — Chunk 9 wires real values
  };
}

export default groupsSlice.reducer;
export const { clearError: clearGroupsError } = groupsSlice.actions;

/* ──────────────────────────────────────────────────────────────────────
 * Selectors
 * ────────────────────────────────────────────────────────────────────── */

export const selectGroups = (s: RootState): GroupListItem[] => s.groups.groups;

export const selectGroupsLoading = (s: RootState) => s.groups.loading;
export const selectGroupsError = (s: RootState) => s.groups.error;
export const selectGroupsHasLoaded = (s: RootState) => s.groups.hasLoaded;

export const selectGroupById =
  (groupId: number | null | undefined) =>
  (s: RootState): GroupListItem | undefined =>
    groupId == null ? undefined : s.groups.groups.find((g) => g.id === groupId);

export const selectGroupDetail =
  (groupId: number | null | undefined) =>
  (s: RootState): GroupDetail | undefined =>
    groupId == null ? undefined : s.groups.detail[groupId];

/** Members in a group with status='ACTIVE' (or OWNER themselves). Used by
 *  GroupDetailScreen + member-pickers in handoff sheets (Chunk 10). */
export const selectActiveMembers =
  (groupId: number | null | undefined) =>
  (s: RootState): GroupMember[] => {
    const detail = groupId == null ? undefined : s.groups.detail[groupId];
    return (detail?.members ?? []).filter(
      (m) => m.status === 'ACTIVE' && m.user_id != null,
    );
  };

/* ── Sprint 2 inventory selectors ─────────────────────────────────── */

/** Cached inventory entry for a group. `undefined` means never loaded. */
export const selectGroupInventory =
  (groupId: number | null | undefined) =>
  (s: RootState): GroupInventoryCacheEntry | undefined =>
    groupId == null ? undefined : s.groups.inventory[groupId];

/** Inventory rows only (or empty array). Convenience for FlatList. */
export const selectGroupInventoryItems =
  (groupId: number | null | undefined) =>
  (s: RootState): GroupItemSummary[] =>
    (groupId == null ? undefined : s.groups.inventory[groupId])?.items ?? [];

/** Sprint 3 / Chunk 10A — fallback for `InventoryItemResponse.groups[]` not
 *  being exposed by backend yet (Gap A / backend#~110). Scans every
 *  cached `groups.inventory[*]` entry and returns the first group_id
 *  whose item list contains `itemId`. Used by CustodySection to derive
 *  a `primaryGroupId` when ItemDetail wasn't navigated to from a Crew
 *  context (i.e. the route param is absent). Returns `null` if the item
 *  isn't in any cached crew (which means either: not bound to a crew,
 *  or the relevant crew's inventory hasn't been fetched yet). */
export const selectFirstGroupForItem =
  (itemId: number | null | undefined) =>
  (s: RootState): number | null => {
    if (itemId == null) return null;
    const cache = s.groups.inventory;
    for (const key of Object.keys(cache)) {
      const groupId = Number(key);
      if (!Number.isFinite(groupId)) continue;
      const items = cache[groupId]?.items ?? [];
      if (items.some((it) => it.item_id === itemId)) {
        return groupId;
      }
    }
    return null;
  };

/** FE-248 v2 follow-up — union of group members across EVERY cached
 *  group detail. CustodySection's name resolution previously only
 *  scanned one group (the route's `primaryGroupId`), which left
 *  cross-group lookups stranded. When the viewer opens an item from
 *  the Items page (no group context in the route) we want every
 *  group the item is bound to contribute its members to the lookup
 *  table. Returns a flat `Record<user_id, GroupMember>` where ties
 *  are broken by latest member record wins (deterministic given
 *  Object.assign iteration order, but the same user can't have
 *  different display names across groups so the tiebreak doesn't
 *  matter functionally). */
export const selectAllCachedMembers = (
  s: RootState,
): Record<number, GroupMember> => {
  const out: Record<number, GroupMember> = {};
  const detail = s.groups.detail;
  for (const key of Object.keys(detail)) {
    const d = detail[Number(key)];
    if (!d || !Array.isArray(d.members)) continue;
    for (const m of d.members) {
      if (m.user_id != null) out[m.user_id] = m;
    }
  }
  return out;
};

/** FE-248 v2 follow-up — fallback hydration for ItemDetail.
 *
 *  When `GET /users/inventory/{id}` returns 404 (the BE#167 regression,
 *  filed as backend#246 — the borrower can't read their borrowed
 *  item via the user-inventory endpoint), look it up in any group
 *  inventory cache the viewer has access to. Returns the row plus
 *  the group it came from, so the FE can flag the limited-view state.
 *
 *  Returns null when:
 *    - itemId is null
 *    - no cached group contains this item
 *
 *  Limitations of the hydrated row:
 *    - shape is `GroupItemSummary`, not `InventoryItemResponse` — has
 *      fewer fields (e.g. no source_image_url, no tags, no
 *      perishability — those live on the full item row)
 *    - sharing_info is absent (no `is_borrowed` flag)
 *
 *  The caller is responsible for rendering a degraded ItemDetail
 *  that's honest about the limited data and offers a CTA to open
 *  the parent group for the full record.
 */
export const selectItemSummaryFromGroups =
  (itemId: number | null | undefined) =>
  (s: RootState): { item: any; groupId: number } | null => {
    if (itemId == null) return null;
    const cache = s.groups.inventory;
    for (const key of Object.keys(cache)) {
      const groupId = Number(key);
      if (!Number.isFinite(groupId)) continue;
      const items = cache[groupId]?.items ?? [];
      const hit = items.find((it) => it.item_id === itemId);
      if (hit) return { item: hit, groupId };
    }
    return null;
  };

/** Sprint 4 / Chunk 10B — best-effort name lookup for a user id by
 *  scanning every cached group's member list. Used by CustodyChip on
 *  Browse cards to render "Dylan" instead of "User #21" without
 *  needing a separate /users/{id} fetch.
 *
 *  Returns the trimmed `name`, or `email` (split before '@'), or null
 *  when no cached group has this user as a member. The caller renders
 *  a generic chip in the null case.
 */
export const selectMemberNameById =
  (userId: number | null | undefined) =>
  (s: RootState): string | null => {
    if (userId == null) return null;
    for (const groupId of Object.keys(s.groups.detail)) {
      const detail = s.groups.detail[Number(groupId)];
      if (!detail?.members) continue;
      for (const m of detail.members) {
        if (m.user_id !== userId) continue;
        if (m.name && m.name.trim()) return m.name.trim();
        if (m.email && m.email.trim()) return m.email.split('@')[0];
      }
    }
    return null;
  };

/* ── Sprint 4 / Chunk 10B activity selectors ─────────────────────── */

/** Cached activity-feed entry for a group. Undefined = never loaded. */
export const selectGroupActivity =
  (groupId: number | null | undefined) =>
  (s: RootState): GroupActivityCacheEntry | undefined =>
    groupId == null ? undefined : s.groups.activity[groupId];

/** Convenience: just the events array (or empty). */
export const selectGroupActivityEvents =
  (groupId: number | null | undefined) =>
  (s: RootState): GroupActivityEvent[] =>
    (groupId == null ? undefined : s.groups.activity[groupId])?.events ?? [];

/* ── backend#114 received-invites selectors ─────────────────────── */
export const selectReceivedInvites = (s: RootState): ReceivedInviteOut[] =>
  s.groups.receivedInvites ?? [];

export const selectReceivedInvitesLoaded = (s: RootState): boolean =>
  s.groups.receivedInvitesLoaded ?? false;

export const selectAcceptingTokens = (s: RootState): string[] =>
  s.groups.acceptingTokens ?? [];
